import { KeyFilter } from "primereact/keyfilter";

/**
 * Global patch for PrimeReact KeyFilter to fix copy/paste and keyboard shortcuts
 * across Windows, macOS, and Linux:
 *
 * 1. Fixes shortcuts (Cmd/Ctrl + C, V, A, X, Z):
 *    - On macOS, shortcuts use metaKey (Command), which PrimeReact failed to check,
 *      causing printable shortcut keys (c, v, a, x) to fail regex and be blocked.
 *    - On Windows/Linux, ensures all Ctrl, Alt, and Meta modifier combinations bypass the filter.
 *
 * 2. Fixes pasting on Windows and macOS:
 *    - In RTL/Arabic environments, text copied from browsers/documents often contains
 *      invisible Unicode directional marks (\u200E, \u200F, \u202A-\u202E) or newlines,
 *      which caused PrimeReact to fail regex validation on those characters and abort paste.
 *    - Cleans these invisible characters, strips 'g' flag state issues, and ensures
 *      valid characters can be pasted smoothly.
 */
if (KeyFilter && KeyFilter.onKeyPress && KeyFilter.onPaste) {
  KeyFilter.onKeyPress = function (e, keyfilter, validateOnly) {
    if (validateOnly) {
      return;
    }

    // Bypass keyfilter for all modifier shortcuts (Ctrl, Cmd/Meta, Alt)
    if (e.ctrlKey || e.metaKey || e.altKey) {
      return;
    }

    // Bypass navigation and control keys (Backspace, Tab, Enter, Arrows, etc.)
    if (e.key && e.key.length > 1) {
      return;
    }

    const regex = this.getRegex(keyfilter);
    if (!regex) return;

    // Use a stateless RegExp to avoid /g flag index tracking issues
    const testRegex =
      regex instanceof RegExp
        ? new RegExp(regex.source, (regex.flags || "").replace("g", ""))
        : regex;

    if (testRegex.test && !testRegex.test(e.key)) {
      e.preventDefault();
    }
  };

  KeyFilter.onPaste = function (e, keyfilter, validateOnly) {
    if (validateOnly) {
      return;
    }

    const regex = this.getRegex(keyfilter);
    if (!regex) return;

    const rawClipboard = e.clipboardData ? e.clipboardData.getData("text") : "";
    if (!rawClipboard) return;

    // Clean invisible formatting characters common in RTL/Arabic text and trim line breaks
    const cleanClipboard = rawClipboard
      .replace(/[\u200B-\u200D\uFEFF\u200E\u200F\u202A-\u202E\r\n]/g, "")
      .trim();

    const testRegex =
      regex instanceof RegExp
        ? new RegExp(regex.source, (regex.flags || "").replace("g", ""))
        : regex;

    let allValid = true;
    for (const c of cleanClipboard) {
      if (testRegex.test && !testRegex.test(c)) {
        allValid = false;
        break;
      }
    }

    if (!allValid || cleanClipboard !== rawClipboard) {
      // Filter out invalid characters and insert valid ones
      const validChars = Array.from(cleanClipboard)
        .filter((c) => (testRegex.test ? testRegex.test(c) : true))
        .join("");

      if (validChars.length > 0) {
        e.preventDefault();
        const target = e.target;
        if (target) {
          const nativeSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype,
            "value"
          )?.set;
          const currentValue = target.value || "";
          const start = target.selectionStart ?? currentValue.length;
          const end = target.selectionEnd ?? currentValue.length;
          const nextValue =
            currentValue.slice(0, start) + validChars + currentValue.slice(end);

          if (nativeSetter) {
            nativeSetter.call(target, nextValue);
          } else {
            target.value = nextValue;
          }

          // Move cursor after the inserted text
          const newCursorPos = start + validChars.length;
          if (typeof target.setSelectionRange === "function") {
            target.setSelectionRange(newCursorPos, newCursorPos);
          }

          target.dispatchEvent(new Event("input", { bubbles: true }));
          target.dispatchEvent(new Event("change", { bubbles: true }));
        }
      } else {
        e.preventDefault();
      }
    }
  };
}
